import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CreditCard, 
  Crown, 
  Users, 
  Calendar,
  TrendingUp,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Settings,
  Tag
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PricingConfig } from './PricingConfig';
import { PromoCodesManager } from './PromoCodesManager';

interface Subscription {
  id: string;
  user_id: string;
  plan: string;
  statut: string;
  date_debut: string;
  date_fin: string | null;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

type SortField = 'user' | 'plan' | 'statut' | 'date_debut' | 'date_fin';
type SortDirection = 'asc' | 'desc';

export const SubscriptionsTable = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [mainTab, setMainTab] = useState<'subscriptions' | 'pricing' | 'promos'>('subscriptions');
  const [showHistory, setShowHistory] = useState(false);
  const [sortField, setSortField] = useState<SortField>('date_debut');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [newPlan, setNewPlan] = useState('');
  const [newStatut, setNewStatut] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      
      const { data: abonnements, error } = await supabase
        .from('abonnements')
        .select(`
          *,
          profils_utilisateur:user_id (
            email,
            prenom,
            nom
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedSubscriptions = abonnements?.map(sub => ({
        ...sub,
        user_email: sub.profils_utilisateur?.email || 'N/A',
        user_name: sub.profils_utilisateur?.prenom && sub.profils_utilisateur?.nom
          ? `${sub.profils_utilisateur.prenom} ${sub.profils_utilisateur.nom}`
          : sub.profils_utilisateur?.email || 'N/A'
      })) || [];

      setSubscriptions(formattedSubscriptions);
    } catch (error) {
      console.error('Erreur lors du chargement des abonnements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dédupliquer : un seul abonnement par utilisateur (le plus récent actif, sinon le plus récent)
  const deduplicatedSubscriptions = useMemo(() => {
    if (showHistory) return subscriptions;
    
    const userMap = new Map<string, Subscription>();
    // Trier par priorité : actif d'abord, puis par date
    const sorted = [...subscriptions].sort((a, b) => {
      if (a.statut === 'actif' && b.statut !== 'actif') return -1;
      if (b.statut === 'actif' && a.statut !== 'actif') return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    
    sorted.forEach(sub => {
      if (!userMap.has(sub.user_id)) {
        userMap.set(sub.user_id, sub);
      }
    });
    
    return Array.from(userMap.values());
  }, [subscriptions, showHistory]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" /> 
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const filteredSubscriptions = useMemo(() => {
    let filtered = [...deduplicatedSubscriptions];
    
    if (activeTab !== 'all') {
      filtered = filtered.filter(sub => sub.plan === activeTab);
    }
    
    return filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'user':
          comparison = (a.user_name || '').localeCompare(b.user_name || '');
          break;
        case 'plan':
          comparison = a.plan.localeCompare(b.plan);
          break;
        case 'statut':
          comparison = a.statut.localeCompare(b.statut);
          break;
        case 'date_debut':
          comparison = new Date(a.date_debut).getTime() - new Date(b.date_debut).getTime();
          break;
        case 'date_fin':
          const aDate = a.date_fin ? new Date(a.date_fin).getTime() : 0;
          const bDate = b.date_fin ? new Date(b.date_fin).getTime() : 0;
          comparison = aDate - bDate;
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [deduplicatedSubscriptions, activeTab, sortField, sortDirection]);

  const stats = useMemo(() => {
    const list = deduplicatedSubscriptions;
    const total = list.length;
    const gratuit = list.filter(s => s.plan === 'gratuit').length;
    const mensuel = list.filter(s => s.plan === 'mensuel').length;
    const annuel = list.filter(s => s.plan === 'annuel').length;
    const actifs = list.filter(s => s.statut === 'actif').length;
    
    return { total, gratuit, mensuel, annuel, actifs };
  }, [deduplicatedSubscriptions]);

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'annuel':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600"><Crown className="h-3 w-3 mr-1" /> Annuel</Badge>;
      case 'mensuel':
        return <Badge className="bg-blue-500 hover:bg-blue-600"><CreditCard className="h-3 w-3 mr-1" /> Mensuel</Badge>;
      default:
        return <Badge variant="secondary">Gratuit</Badge>;
    }
  };

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case 'actif':
        return <Badge className="bg-green-500 hover:bg-green-600">Actif</Badge>;
      case 'expiré':
        return <Badge variant="destructive">Expiré</Badge>;
      case 'annulé':
        return <Badge variant="outline" className="text-red-500 border-red-500">Annulé</Badge>;
      default:
        return <Badge variant="outline">{statut}</Badge>;
    }
  };

  const handleEditClick = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setNewPlan(subscription.plan);
    setNewStatut(subscription.statut);
  };

  const handleUpdateSubscription = async () => {
    if (!editingSubscription) return;

    try {
      const now = new Date();
      let dateFin: string | null = null;

      if (newPlan === 'mensuel') {
        const end = new Date(now);
        end.setMonth(end.getMonth() + 1);
        dateFin = end.toISOString();
      } else if (newPlan === 'annuel') {
        const end = new Date(now);
        end.setFullYear(end.getFullYear() + 1);
        dateFin = end.toISOString();
      } else if (newPlan === 'partenaire') {
        const end = new Date(now);
        end.setMonth(end.getMonth() + 3);
        dateFin = end.toISOString();
      }
      // 'gratuit' → date_fin = null

      const { error } = await supabase
        .from('abonnements')
        .update({
          plan: newPlan,
          statut: newStatut,
          date_debut: now.toISOString(),
          date_fin: dateFin,
          updated_at: now.toISOString()
        })
        .eq('id', editingSubscription.id);

      if (error) throw error;

      toast.success('Abonnement mis à jour avec succès');
      setEditingSubscription(null);
      fetchSubscriptions();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast.error('Erreur lors de la mise à jour de l\'abonnement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Chargement des abonnements...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-nautic">Gestion des abonnements</h1>
        <div className="flex items-center gap-2">
        <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'subscriptions' | 'pricing' | 'promos')}>
          <TabsList>
            <TabsTrigger value="subscriptions" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Abonnements
            </TabsTrigger>
            <TabsTrigger value="promos" className="flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Codes promo
            </TabsTrigger>
            <TabsTrigger value="pricing" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Prix
            </TabsTrigger>
          </TabsList>
          </Tabs>
          <Button 
            variant={showHistory ? "default" : "outline"} 
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            <Calendar className="h-4 w-4 mr-1" />
            {showHistory ? 'Vue unique' : 'Historique'}
          </Button>
        </div>
      </div>

      {mainTab === 'pricing' ? (
        <PricingConfig />
      ) : mainTab === 'promos' ? (
        <PromoCodesManager />
      ) : (
        <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Users className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gratuit</p>
                <p className="text-2xl font-bold">{stats.gratuit}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mensuel</p>
                <p className="text-2xl font-bold">{stats.mensuel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Crown className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Annuel</p>
                <p className="text-2xl font-bold">{stats.annuel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Actifs</p>
                <p className="text-2xl font-bold text-green-600">{stats.actifs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Table */}
      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="all">Tous ({stats.total})</TabsTrigger>
              <TabsTrigger value="gratuit">Gratuit ({stats.gratuit})</TabsTrigger>
              <TabsTrigger value="mensuel">Mensuel ({stats.mensuel})</TabsTrigger>
              <TabsTrigger value="annuel">Annuel ({stats.annuel})</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('user')}
                  >
                    <div className="flex items-center">
                      Utilisateur {getSortIcon('user')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('plan')}
                  >
                    <div className="flex items-center">
                      Plan {getSortIcon('plan')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('statut')}
                  >
                    <div className="flex items-center">
                      Statut {getSortIcon('statut')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('date_debut')}
                  >
                    <div className="flex items-center">
                      Date début {getSortIcon('date_debut')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('date_fin')}
                  >
                    <div className="flex items-center">
                      Date fin {getSortIcon('date_fin')}
                    </div>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscriptions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Aucun abonnement trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{subscription.user_name}</p>
                          <p className="text-sm text-muted-foreground">{subscription.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(subscription.plan)}</TableCell>
                      <TableCell>{getStatutBadge(subscription.statut)}</TableCell>
                      <TableCell>
                        {format(new Date(subscription.date_debut), 'dd MMM yyyy', { locale: fr })}
                      </TableCell>
                      <TableCell>
                        {subscription.date_fin 
                          ? format(new Date(subscription.date_fin), 'dd MMM yyyy', { locale: fr })
                          : <span className="text-muted-foreground">-</span>
                        }
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditClick(subscription)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingSubscription} onOpenChange={(open) => !open && setEditingSubscription(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'abonnement</DialogTitle>
          </DialogHeader>
          
          {editingSubscription && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">Utilisateur</p>
                <p className="text-muted-foreground">{editingSubscription.user_name} ({editingSubscription.user_email})</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Plan</label>
                <Select value={newPlan} onValueChange={setNewPlan}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gratuit">Gratuit</SelectItem>
                    <SelectItem value="mensuel">Mensuel (1 mois)</SelectItem>
                    <SelectItem value="annuel">Annuel (1 an)</SelectItem>
                    <SelectItem value="partenaire">Partenaire (3 mois)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Statut</label>
                <Select value={newStatut} onValueChange={setNewStatut}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="expiré">Expiré</SelectItem>
                    <SelectItem value="annulé">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSubscription(null)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateSubscription}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};
